import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Landmark,
  Users,
  Globe2,
  Handshake,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const FamilyOfficeAdvisory = () => {
  const mandantenLeistungen = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Persönliche Begleitung',
      description:
        'Durch erfahrene Spezialisten — kein Callcenter, kein anonymes Banking',
    },
    {
      icon: <Globe2 className="w-8 h-8" />,
      title: 'Internationales Netzwerk',
      description:
        'Zugang zu Analyse- und Vermögensverwaltungspartnern weltweit',
    },
    {
      icon: <Handshake className="w-8 h-8" />,
      title: 'Abstimmung mit Beratern',
      description:
        'Steuerliche und rechtliche Koordination mit den bestehenden Beratern unserer Mandanten',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col">
              <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 text-brand-700 font-medium">
                <Landmark className="w-5 h-5" />
                <span>Family Office Advisory</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-brand-900 leading-tight">
                Family Office Advisory
              </h1>
              <p className="text-xl text-brand-700 leading-relaxed">
                Für anspruchsvolle Privatmandanten bieten wir eine umfassende Family-Office-Leistung:
                von der individuellen Anlagestrategie über die Strukturierung von Beteiligungen bis
                zur generationenübergreifenden Vermögensplanung.
              </p>
              </div>
              <div className="mt-10">
              <Link to="/kontakt">
                <Button
                  size="lg"
                  className="bg-brand-900 hover:bg-brand-800 text-white px-8 py-3 text-base transition-all duration-300 hover:shadow-lg"
                >
                  Beratung anfragen
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              </div>
            </div>
            <div className="relative h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf"
                alt="Persönliche Beratung im Family Office"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-900/20 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Was unsere Mandanten erhalten */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-brand-900 mb-4">
              Was unsere Mandanten erhalten
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mandantenLeistungen.map((item, index) => (
              <Card
                key={index}
                className="border-2 border-brand-200 hover:border-brand-400 transition-all duration-300 hover:shadow-lg"
              >
                <CardHeader className="p-8 text-center">
                  <div className="text-brand-700 mb-4 flex justify-center">{item.icon}</div>
                  <CardTitle className="text-2xl mb-3">{item.title}</CardTitle>
                  <CardDescription className="text-base text-brand-600 leading-relaxed">
                    {item.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Warum DR. GRASSL & Coll */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-brand-900 mb-6">
                Warum DR. GRASSL <span className="text-brand-600">&</span> Coll.
              </h2>
              <p className="text-lg text-brand-700 leading-relaxed mb-6">
                Wir sind keine Bank und kein Fondsvertrieb. Wir sind eine unabhängige
                Wirtschaftskanzlei mit über 40 Jahren Praxis an der Schnittstelle zwischen
                Immobilienwirtschaft, Kapitalmarkt und Wirtschaftsrecht.
              </p>
              <p className="text-lg text-brand-700 leading-relaxed">
                Diese Kombination ist selten — und für unsere Mandanten ein entscheidender Vorteil:
                Wir beurteilen Investments nicht isoliert, sondern immer im Kontext des gesamten
                Vermögens, der steuerlichen Situation und der persönlichen Lebensplanung.
              </p>
            </div>
            <div className="relative h-[500px] lg:h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab"
                alt="Moderne Architektur und Substanz"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vertrauen Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-900 mb-8">
            Vertrauen durch einen nachvollziehbaren Prozess
          </h2>
          <p className="text-xl text-brand-800 leading-relaxed">
            Vertrauen entsteht nicht durch Versprechen, sondern durch einen nachvollziehbaren Prozess.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center gap-8">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-900">
            Lassen Sie uns über Ihr Vermögen sprechen
          </h2>
          <p className="text-xl text-brand-700 max-w-3xl">
            Wir freuen uns auf ein persönliches Gespräch.
          </p>
          <Link to="/kontakt">
            <Button
              size="lg"
              className="bg-brand-900 hover:bg-brand-800 text-white px-10 py-3 text-lg transition-all duration-300 hover:shadow-lg"
            >
              Kontakt aufnehmen
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FamilyOfficeAdvisory;
